<template>
  <SettingsPageLayout>
    <div class="mx-auto max-w-5xl">
      <div class="mb-4 flex justify-between items-center">
        <h1 class="block text-2xl font-bold leading-6 text-gray-900">Sms Templates</h1>
        <Button @click="showTemplateCreate = true">
          New
          <vue-feather type="plus-circle" class="h-4 w-4" />
        </Button>
      </div>
      <SearchInput v-model="search" placeholder="Search templates" class="mb-4" />

      <HFetchQuery :query="query">
        <template #loading>
          <div class="flex justify-center py-16">
            <div class="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
          </div>
        </template>
        <template #default="{ data }">
          <div
            v-if="filterTemplates(data).length"
            class="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200"
          >
            <TemplateRow
              v-for="template of filterTemplates(data)"
              :key="template.id"
              :template="{ type: 'sms', data: template }"
              @click-delete="
                selectedTemplate = template;
                showTemplateDelete = true;
              "
              @click-edit="
                selectedTemplate = template;
                showTemplateEdit = true;
              "
            />
          </div>
          <p v-else class="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-500">
            {{ search ? `No templates match "${search}".` : 'There are no SMS templates yet.' }}
          </p>
        </template>
      </HFetchQuery>
    </div>

    <SmsTemplateCreate
      v-if="showTemplateCreate"
      @handle-cancel-settings="showTemplateCreate = false"
      @handle-save-settings="
        query.reload();
        showTemplateCreate = false;
      "
    />
    <SmsTemplateEdit
      v-if="showTemplateEdit"
      :selected-template="selectedTemplate"
      @handle-cancel-settings="showTemplateEdit = false"
      @handle-save-settings="
        query.reload();
        showTemplateEdit = false;
      "
    />
    <SmsTemplateDelete
      v-if="showTemplateDelete"
      :selected-template-id="selectedTemplate?.id"
      @handle-template-delete-cancel="showTemplateDelete = false"
      @handle-save-settings="
        query.reload();
        showTemplateDelete = false;
      "
      @handle-in-use="
        showTemplateDelete = false;
        showInUseByWorkflowModal = true;
      "
    />
    <InUseByWorkflowModal
      v-if="showInUseByWorkflowModal"
      :name="selectedTemplate?.name ?? ''"
      kind="sms"
      @handle-cancel="showInUseByWorkflowModal = false"
    />
  </SettingsPageLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import SettingsPageLayout from '../../layouts/SettingsPageLayout/index.vue';
import SmsTemplateEdit from './SmsTemplateEdit.vue';
import SmsTemplateDelete from './SmsTemplateDelete.vue';
import SmsTemplateCreate from './SmsTemplateCreate.vue';
import TemplateRow from './TemplateRow.vue';
import InUseByWorkflowModal from './InUseByWorkflowModal.vue';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import { Api } from '@/api';
import type { SmsTemplateEntity } from '@/api/entity_types/SmsTemplate';

const search = ref('');
const showTemplateEdit = ref(false);
const showTemplateDelete = ref(false);
const showTemplateCreate = ref(false);
const showInUseByWorkflowModal = ref(false);
const selectedTemplate = ref<SmsTemplateEntity | null>(null);

const query = useHFetchQuery<SmsTemplateEntity[]>(async () => {
  const response = await Api.SmsTemplates.listSmsTemplates();
  return { status: 'success' as const, data: response?.records ?? [] };
});

const filterTemplates = (templates: SmsTemplateEntity[]) => {
  const term = search.value.trim().toLowerCase();
  if (!term) return templates;
  return templates.filter(
    template => template.name.toLowerCase().includes(term) || (template.body || '').toLowerCase().includes(term)
  );
};
</script>
